// Locations of self-hosted engines and helpers to download them ahead of use
// so they are available offline (the service worker stores them).

export type EngineId = 'pdfjs' | 'qpdf' | 'ocr' | 'gs' | 'mupdf' | 'office';

/** Engines that are downloaded on first use (not precached with the app). */
export type OptionalEngine = 'ocr' | 'gs' | 'mupdf' | 'office';
export const OPTIONAL_ENGINES: OptionalEngine[] = ['ocr', 'gs', 'mupdf', 'office'];

export const VENDOR_CACHE = 'ns-vendor-v1';

export function vendor(id: EngineId) {
  const info = __VENDOR__[id];
  if (!info) throw new Error(`Engine ${id} is not part of this build`);
  return info;
}

export function hasEngine(id: EngineId): boolean {
  return Boolean(__VENDOR__[id]);
}

export function absoluteUrl(path: string): string {
  return new URL(path, location.origin).href;
}

// Same feature tests as wasm-feature-detect (used by Tesseract to pick its core build).
const SIMD = new Uint8Array([0, 97, 115, 109, 1, 0, 0, 0, 1, 5, 1, 96, 0, 1, 123, 3, 2, 1, 0, 10, 10, 1, 8, 0, 65, 0, 253, 15, 253, 98, 11]);
const RELAXED_SIMD = new Uint8Array([0, 97, 115, 109, 1, 0, 0, 0, 1, 5, 1, 96, 0, 1, 123, 3, 2, 1, 0, 10, 15, 1, 13, 0, 65, 1, 253, 15, 65, 2, 253, 15, 253, 128, 2, 11]);

function tesseractCoreName(): string {
  try {
    if (WebAssembly.validate(RELAXED_SIMD)) return 'tesseract-core-relaxedsimd-lstm';
    if (WebAssembly.validate(SIMD)) return 'tesseract-core-simd-lstm';
  } catch {
    // fall through
  }
  return 'tesseract-core-lstm';
}

/** Files this browser actually needs (OCR ships three core builds, only one is used). */
export function requiredFiles(id: EngineId): Array<[string, number]> {
  const files = vendor(id).files;
  if (id !== 'ocr') return files;
  const core = `/core/${tesseractCoreName()}.wasm.js`;
  return files.filter(([url]) => !url.includes('/core/') || url.endsWith(core));
}

/** Approximate download size (files are served compressed). */
const TRANSFER_RATIO: Record<EngineId, number> = { office: 0.25, ocr: 0.8, gs: 0.67, mupdf: 0.35, pdfjs: 0.5, qpdf: 0.45 };

export function downloadSize(id: EngineId): number {
  return Math.round(requiredFiles(id).reduce((s, [, size]) => s + size, 0) * TRANSFER_RATIO[id]);
}

/** True when every file the engine needs is in the offline cache. */
export async function isEngineCached(id: EngineId): Promise<boolean> {
  if (!('caches' in self) || !hasEngine(id)) return false;
  try {
    const cache = await caches.open(VENDOR_CACHE);
    for (const [url] of requiredFiles(id)) {
      if (!(await cache.match(url))) return false;
    }
    return true;
  } catch {
    return false;
  }
}

/**
 * Downloads every file an engine needs, reporting progress, and keeps them in
 * the offline cache. When a service worker controls the page it stores the
 * responses itself; otherwise the stream is teed into the cache here.
 */
export async function prefetchEngine(id: EngineId, onProgress?: (loaded: number, total: number) => void, signal?: AbortSignal): Promise<void> {
  const files = requiredFiles(id);
  const total = files.reduce((s, [, size]) => s + size, 0);
  const controlled = Boolean(navigator.serviceWorker?.controller);
  let cache: Cache | null = null;
  if (!controlled && 'caches' in self) {
    try {
      cache = await caches.open(VENDOR_CACHE);
    } catch {
      cache = null; // storage unavailable (private mode): the HTTP cache still helps
    }
  }
  let loaded = 0;
  for (const [url, size] of files) {
    if (cache && (await cache.match(url))) {
      loaded += size;
      onProgress?.(loaded, total);
      continue;
    }
    const res = await fetch(url, { signal });
    if (!res.ok || !res.body) throw new Error(`HTTP ${res.status} for ${url}`);
    let body = res.body;
    let stored: Promise<void> | null = null;
    if (cache) {
      const [forProgress, forCache] = body.tee();
      body = forProgress;
      stored = cache.put(url, new Response(forCache, { headers: res.headers })).catch(() => undefined);
    }
    const reader = body.getReader();
    let fileLoaded = 0;
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      fileLoaded += value.byteLength;
      // Compressed transfers report decoded bytes; cap at the file size.
      onProgress?.(loaded + Math.min(fileLoaded, size), total);
    }
    await stored;
    if (controlled) await waitForCache(url);
    loaded += size;
    onProgress?.(loaded, total);
  }
}

/** The service worker stores responses in the background: wait until it has. */
async function waitForCache(url: string, timeoutMs = 60_000): Promise<void> {
  if (!('caches' in self)) return;
  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    try {
      if (await caches.match(url, { cacheName: VENDOR_CACHE })) return;
    } catch {
      return;
    }
    await new Promise((r) => setTimeout(r, 250));
  }
}

export async function removeEngine(id: EngineId): Promise<void> {
  if (!('caches' in self) || !hasEngine(id)) return;
  const cache = await caches.open(VENDOR_CACHE);
  for (const [url] of vendor(id).files) await cache.delete(url);
}
